import { useState, useCallback, useRef, useEffect } from 'react';

export type BuilderState = {
  sections: any[];
  settingsData: Record<string, any>;
};

type HistoryState = {
  past: BuilderState[];
  present: BuilderState;
  future: BuilderState[];
};

const MAX_HISTORY = 50;
const NORMAL_DEBOUNCE_MS = 2000;
const FAST_DEBOUNCE_MS = 250;

export function useBuilderState({
  initialState,
  templateType,
  onSave,
}: {
  initialState: BuilderState;
  templateType: string;
  onSave: (state: BuilderState) => Promise<void>;
}) {
  // Store history for each template type independently
  const [histories, setHistories] = useState<Record<string, HistoryState>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // References for debouncing
  const normalSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const fastSaveTimer = useRef<NodeJS.Timeout | null>(null);
  
  const activeHistory = histories[templateType] || {
    past: [],
    present: initialState,
    future: [],
  };

  // Initialize if empty (e.g. on first load or template switch)
  useEffect(() => {
    if (!histories[templateType]) {
      setHistories((prev) => ({
        ...prev,
        [templateType]: { past: [], present: initialState, future: [] },
      }));
    }
  }, [templateType, initialState, histories]);

  const executeSave = async (stateToSave: BuilderState) => {
    setSaveStatus('saving');
    try {
      await onSave(stateToSave);
      setSaveStatus('saved');
      setTimeout(() => {
        setSaveStatus((current) => (current === 'saved' ? 'idle' : current));
      }, 2000);
    } catch (err) {
      setSaveStatus('error');
    }
  };

  const triggerNormalSave = (state: BuilderState) => {
    if (normalSaveTimer.current) clearTimeout(normalSaveTimer.current);
    if (fastSaveTimer.current) clearTimeout(fastSaveTimer.current);
    
    setSaveStatus('idle');
    normalSaveTimer.current = setTimeout(() => {
      executeSave(state);
    }, NORMAL_DEBOUNCE_MS);
  };

  const triggerFastSave = (state: BuilderState) => {
    if (normalSaveTimer.current) clearTimeout(normalSaveTimer.current);
    if (fastSaveTimer.current) clearTimeout(fastSaveTimer.current);
    
    setSaveStatus('idle');
    fastSaveTimer.current = setTimeout(() => {
      executeSave(state);
    }, FAST_DEBOUNCE_MS);
  };

  const setState = useCallback(
    (newState: BuilderState | ((prev: BuilderState) => BuilderState)) => {
      setHistories((prevHistories) => {
        const currentHist = prevHistories[templateType] || { past: [], present: initialState, future: [] };
        const resolvedNewState = typeof newState === 'function' ? newState(currentHist.present) : newState;
        
        const newPast = [...currentHist.past, currentHist.present].slice(-MAX_HISTORY);
        
        triggerNormalSave(resolvedNewState);

        return {
          ...prevHistories,
          [templateType]: {
            past: newPast,
            present: resolvedNewState,
            future: [],
          },
        };
      });
    },
    [templateType, initialState]
  );

  const undo = useCallback(() => {
    setHistories((prevHistories) => {
      const currentHist = prevHistories[templateType];
      if (!currentHist || currentHist.past.length === 0) return prevHistories;

      const newPast = [...currentHist.past];
      const newPresent = newPast.pop()!;
      const newFuture = [currentHist.present, ...currentHist.future];

      triggerFastSave(newPresent);

      return {
        ...prevHistories,
        [templateType]: {
          past: newPast,
          present: newPresent,
          future: newFuture,
        },
      };
    });
  }, [templateType]);

  const redo = useCallback(() => {
    setHistories((prevHistories) => {
      const currentHist = prevHistories[templateType];
      if (!currentHist || currentHist.future.length === 0) return prevHistories;

      const newFuture = [...currentHist.future];
      const newPresent = newFuture.shift()!;
      const newPast = [...currentHist.past, currentHist.present].slice(-MAX_HISTORY);

      triggerFastSave(newPresent);

      return {
        ...prevHistories,
        [templateType]: {
          past: newPast,
          present: newPresent,
          future: newFuture,
        },
      };
    });
  }, [templateType]);

  const retrySave = () => {
    executeSave(activeHistory.present);
  };

  const resetState = useCallback((newState: BuilderState) => {
    setHistories((prev) => ({
      ...prev,
      [templateType]: { past: [], present: newState, future: [] },
    }));
  }, [templateType]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          redo();
        } else {
          e.preventDefault();
          undo();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  return {
    state: activeHistory.present,
    setState,
    undo,
    redo,
    canUndo: activeHistory.past.length > 0,
    canRedo: activeHistory.future.length > 0,
    saveStatus,
    retrySave,
    resetState,
  };
}
